// Menu input: last-used input device tracking (for key glyphs), raw gamepad
// polling, and MenuNav — focus navigation shared by every screen so that each
// one is fully usable with keyboard, mouse and gamepad.

import Phaser from 'phaser';
import { save } from '../core/save';
import { sfx, sfxHover, unlockAudio } from './sound';

// ───────────────────────────── Input mode ─────────────────────────────

export type InputMode = 'kbm' | 'pad';

let mode: InputMode = 'kbm';
const modeListeners = new Set<(m: InputMode) => void>();
let domInstalled = false;

export function getInputMode(): InputMode {
  return mode;
}

export function setInputMode(m: InputMode): void {
  if (m === mode) return;
  mode = m;
  for (const fn of Array.from(modeListeners)) {
    try {
      fn(m);
    } catch (err) {
      console.error('[ui] input-mode listener failed', err);
    }
  }
}

/** Subscribe to keyboard/mouse ↔ gamepad switches. Returns an unsubscribe function. */
export function onInputModeChange(fn: (m: InputMode) => void): () => void {
  modeListeners.add(fn);
  return () => modeListeners.delete(fn);
}

/** Window-level listeners (installed once): any key / click / real mouse movement means keyboard+mouse, and unlocks audio. */
export function installInputTracking(): void {
  if (domInstalled || typeof window === 'undefined') return;
  domInstalled = true;
  const kbm = (): void => {
    unlockAudio();
    setInputMode('kbm');
  };
  window.addEventListener('keydown', kbm, true);
  window.addEventListener('pointerdown', kbm, true);
  let lx = Number.NaN;
  let ly = Number.NaN;
  window.addEventListener(
    'pointermove',
    (e: PointerEvent) => {
      if (!Number.isNaN(lx) && Math.abs(e.clientX - lx) + Math.abs(e.clientY - ly) > 8) setInputMode('kbm');
      lx = e.clientX;
      ly = e.clientY;
    },
    true,
  );
}

// ───────────────────────────── Gamepad polling ─────────────────────────────

/** Standard-mapping button indices. */
export const PAD = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  LB: 4,
  RB: 5,
  LT: 6,
  RT: 7,
  BACK: 8,
  START: 9,
  LS: 10,
  RS: 11,
  UP: 12,
  DOWN: 13,
  LEFT: 14,
  RIGHT: 15,
} as const;

export interface PadState {
  connected: boolean;
  buttons: boolean[];
  /** Left stick after deadzone, -1..1. */
  lx: number;
  ly: number;
}

const EMPTY_PAD: PadState = { connected: false, buttons: new Array(17).fill(false), lx: 0, ly: 0 };

/** Merge every connected standard gamepad into one state (any pad can drive the menus). */
export function readPads(): PadState {
  let pads: (Gamepad | null)[] = [];
  try {
    pads = typeof navigator !== 'undefined' && navigator.getGamepads ? Array.from(navigator.getGamepads()) : [];
  } catch {
    pads = [];
  }
  let connected = false;
  const buttons = new Array(17).fill(false) as boolean[];
  let lx = 0;
  let ly = 0;
  const dz = Phaser.Math.Clamp(save.settings.deadzone ?? 0.2, 0.05, 0.6);
  for (const p of pads) {
    if (!p || !p.connected) continue;
    connected = true;
    for (let i = 0; i < Math.min(17, p.buttons.length); i++) {
      const b = p.buttons[i];
      if (b && (b.pressed || b.value > 0.5)) buttons[i] = true;
    }
    const ax = p.axes[0] ?? 0;
    const ay = p.axes[1] ?? 0;
    if (Math.hypot(ax, ay) > dz && Math.hypot(ax, ay) > Math.hypot(lx, ly)) {
      lx = ax;
      ly = ay;
    }
  }
  if (!connected) return EMPTY_PAD;
  if (buttons.some(Boolean) || Math.hypot(lx, ly) > 0.5) setInputMode('pad');
  return { connected, buttons, lx, ly };
}

// ───────────────────────────── MenuNav ─────────────────────────────

export type NavDir = 'up' | 'down' | 'left' | 'right';

export interface NavRect {
  /** Centre. */
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Anything focusable: buttons, sliders, cards, map nodes. Coordinates are screen space. */
export interface NavItem {
  navBounds(): NavRect;
  setFocused(on: boolean): void;
  /** Confirm pressed / clicked while focused. */
  activate(): void;
  /** Left/right adjust (sliders, steppers, carousels). Return true if consumed. */
  adjust?(dir: -1 | 1): boolean;
  /** False → activation plays the deny sound instead (item stays focusable so players can read why). */
  isEnabled?(): boolean;
  /** False → skipped by navigation entirely (hidden items). */
  isFocusable?(): boolean;
}

export interface MenuNavOptions {
  /** Esc / Backspace / gamepad B. */
  onBack?: () => void;
  /** Wrap to the far side when nothing lies in the pressed direction (default true). */
  wrap?: boolean;
  /** 'spatial' (default) picks the nearest item in the pressed direction; 'vertical'/'horizontal' step through the list order. */
  layout?: 'spatial' | 'vertical' | 'horizontal';
  /** Keyboard codes that confirm (default Enter, NumpadEnter, Space, KeyE). */
  confirmKeys?: string[];
  /** Extra keyboard handlers by KeyboardEvent.code (checked first). */
  keys?: Record<string, (e: KeyboardEvent) => void>;
  /** Extra gamepad button handlers by PAD index (checked before A/B). */
  pad?: Partial<Record<number, () => void>>;
  /** Called whenever focus changes. */
  onFocus?: (item: NavItem | null) => void;
}

const DIR_KEYS: Record<string, NavDir> = {
  ArrowUp: 'up',
  KeyW: 'up',
  ArrowDown: 'down',
  KeyS: 'down',
  ArrowLeft: 'left',
  KeyA: 'left',
  ArrowRight: 'right',
  KeyD: 'right',
};

const REPEAT_DELAY = 380;
const REPEAT_RATE = 115;

/**
 * Focus navigation for one screen (or one modal layer). Keyboard events are
 * consumed with stopPropagation so scenes further down the stack (the paused
 * game, the HUD) never see them; gamepads are polled in update().
 */
export class MenuNav {
  readonly items: NavItem[] = [];
  private index = -1;
  private enabledFlag = true;
  private lockUntil = 0;
  private prevButtons: boolean[] = new Array(17).fill(false);
  private prevDir: NavDir | null = null;
  private dirHeldSince = 0;
  private lastRepeat = 0;
  private downItem: NavItem | null = null;
  private readonly opts: MenuNavOptions;
  private readonly confirmKeys: Set<string>;
  private destroyed = false;

  constructor(
    readonly scene: Phaser.Scene,
    opts: MenuNavOptions = {},
  ) {
    this.opts = opts;
    this.confirmKeys = new Set(opts.confirmKeys ?? ['Enter', 'NumpadEnter', 'Space', 'KeyE']);
    installInputTracking();
    scene.input.keyboard?.on('keydown', this.onKey, this);
    scene.events.on(Phaser.Scenes.Events.RESUME, this.resync, this);
    scene.events.on(Phaser.Scenes.Events.WAKE, this.resync, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.resync();
  }

  get enabled(): boolean {
    return this.enabledFlag;
  }

  set enabled(on: boolean) {
    if (on && !this.enabledFlag) this.resync();
    this.enabledFlag = on;
  }

  get focused(): NavItem | null {
    return this.items[this.index] ?? null;
  }

  get focusedIndex(): number {
    return this.index;
  }

  add<T extends NavItem>(item: T): T {
    this.items.push(item);
    return item;
  }

  remove(item: NavItem): void {
    const i = this.items.indexOf(item);
    if (i < 0) return;
    if (i === this.index) {
      item.setFocused(false);
      this.index = -1;
    } else if (i < this.index) {
      this.index--;
    }
    this.items.splice(i, 1);
  }

  clear(): void {
    this.focused?.setFocused(false);
    this.items.length = 0;
    this.index = -1;
  }

  /** Ignore input for a short time (after opening, so the key that opened a screen cannot also trigger it). */
  lock(ms = 160): void {
    this.lockUntil = Math.max(this.lockUntil, performance.now() + ms);
  }

  /** Treat currently held gamepad buttons as already handled (after resume/wake). */
  resync(): void {
    const s = readPads();
    this.prevButtons = s.buttons.slice();
    this.prevDir = this.stickDir(s);
    this.dirHeldSince = performance.now();
    this.lock(140);
  }

  focus(target: NavItem | number | null, opts: { silent?: boolean } = {}): void {
    const i = target === null ? -1 : typeof target === 'number' ? target : this.items.indexOf(target);
    if (i === this.index) return;
    this.focused?.setFocused(false);
    this.index = i >= 0 && i < this.items.length ? i : -1;
    const f = this.focused;
    if (f) {
      f.setFocused(true);
      if (!opts.silent) sfxHover();
    }
    this.opts.onFocus?.(f);
  }

  /** Focus the first focusable item (or keep the current one if still valid). */
  ensureFocus(): void {
    const f = this.focused;
    if (f && this.focusable(f)) return;
    const first = this.items.find((it) => this.focusable(it));
    if (first) this.focus(first, { silent: true });
  }

  /** Activate an item (mouse click or confirm). */
  activate(item: NavItem | null = this.focused): void {
    if (!item || !this.enabledFlag) return;
    if (item.isEnabled && !item.isEnabled()) {
      sfx('ui_deny');
      return;
    }
    item.activate();
  }

  back(): void {
    if (!this.enabledFlag) return;
    this.opts.onBack?.();
  }

  move(dir: NavDir): void {
    const cur = this.focused;
    if (cur && (dir === 'left' || dir === 'right') && cur.adjust?.(dir === 'left' ? -1 : 1)) return;
    if (cur && (dir === 'up' || dir === 'down') && this.opts.layout === 'horizontal' && cur.adjust?.(dir === 'up' ? 1 : -1)) return;
    const candidates = this.items.filter((it) => it !== cur && this.focusable(it));
    if (!cur || !this.focusable(cur)) {
      if (candidates.length) this.focus(candidates[0]);
      return;
    }
    const layout = this.opts.layout ?? 'spatial';
    if (layout !== 'spatial') {
      const forward = layout === 'vertical' ? dir === 'down' : dir === 'right';
      const backward = layout === 'vertical' ? dir === 'up' : dir === 'left';
      if (!forward && !backward) return;
      const list = this.items.filter((it) => this.focusable(it));
      let i = list.indexOf(cur) + (forward ? 1 : -1);
      if (i < 0 || i >= list.length) {
        if (this.opts.wrap === false) return;
        i = (i + list.length) % list.length;
      }
      this.focus(list[i]);
      return;
    }
    const best = this.spatialPick(cur, dir, candidates) ?? (this.opts.wrap === false ? null : this.wrapPick(cur, dir, candidates));
    if (best) this.focus(best);
  }

  /** Mouse support: hover focuses, click (down + up on the same item) activates. */
  bindPointer(item: NavItem, target: Phaser.GameObjects.GameObject, onPress?: (down: boolean) => void): void {
    target.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, () => {
      if (this.enabledFlag && this.focusable(item)) this.focus(item);
    });
    target.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, (p: Phaser.Input.Pointer) => {
      if (!this.enabledFlag || p.button !== 0) return;
      this.downItem = item;
      onPress?.(true);
    });
    target.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, () => {
      if (this.downItem === item) onPress?.(false);
      if (this.downItem === item) this.downItem = null;
    });
    target.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, (p: Phaser.Input.Pointer) => {
      if (p.button !== 0) return;
      const wasDown = this.downItem === item;
      this.downItem = null;
      onPress?.(false);
      if (!wasDown || !this.enabledFlag || performance.now() < this.lockUntil) return;
      this.focus(item, { silent: true });
      this.activate(item);
    });
  }

  /** Poll the gamepad. Call from the scene's update(). */
  update(): void {
    if (this.destroyed) return;
    const s = readPads();
    const now = performance.now();
    const pressed = (i: number): boolean => s.buttons[i] && !this.prevButtons[i];
    const active = this.enabledFlag && now >= this.lockUntil;

    if (active && s.connected) {
      let handled = false;
      for (const [k, fn] of Object.entries(this.opts.pad ?? {})) {
        if (fn && pressed(Number(k))) {
          fn();
          handled = true;
          break;
        }
      }
      if (!handled) {
        if (pressed(PAD.A)) this.activate();
        else if (pressed(PAD.B)) this.back();
      }
    }

    // Directions: d-pad or left stick, with key-repeat while held.
    const dir = this.stickDir(s);
    if (dir !== this.prevDir) {
      this.prevDir = dir;
      this.dirHeldSince = now;
      this.lastRepeat = now;
      if (dir && active) this.move(dir);
    } else if (dir && active && now - this.dirHeldSince > REPEAT_DELAY && now - this.lastRepeat > REPEAT_RATE) {
      this.lastRepeat = now;
      this.move(dir);
    }
    this.prevButtons = s.buttons.slice();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.input?.keyboard?.off('keydown', this.onKey, this);
    this.scene.events.off(Phaser.Scenes.Events.RESUME, this.resync, this);
    this.scene.events.off(Phaser.Scenes.Events.WAKE, this.resync, this);
    this.items.length = 0;
  }

  // ───────────── internals ─────────────

  private onKey(e: KeyboardEvent): void {
    if (this.destroyed || !this.enabledFlag) return;
    if (performance.now() < this.lockUntil) {
      this.consume(e);
      return;
    }
    const custom = this.opts.keys?.[e.code];
    if (custom) {
      if (!e.repeat) custom(e);
      this.consume(e);
      return;
    }
    const dir = DIR_KEYS[e.code];
    if (dir) {
      this.move(dir);
      this.consume(e);
      return;
    }
    if (this.confirmKeys.has(e.code)) {
      if (!e.repeat) this.activate();
      this.consume(e);
      return;
    }
    if (e.code === 'Escape' || e.code === 'Backspace') {
      if (!e.repeat) this.back();
      this.consume(e);
      return;
    }
    if (e.code === 'Tab') {
      this.move(e.shiftKey ? 'up' : 'down');
      this.consume(e);
    }
  }

  /** Stop the event reaching scenes further down the list (and the browser). */
  private consume(e: KeyboardEvent): void {
    e.preventDefault?.();
    e.stopPropagation?.();
  }

  private stickDir(s: PadState): NavDir | null {
    if (!s.connected) return null;
    if (s.buttons[PAD.UP]) return 'up';
    if (s.buttons[PAD.DOWN]) return 'down';
    if (s.buttons[PAD.LEFT]) return 'left';
    if (s.buttons[PAD.RIGHT]) return 'right';
    const { lx, ly } = s;
    if (Math.hypot(lx, ly) < 0.55) return null;
    return Math.abs(lx) > Math.abs(ly) ? (lx < 0 ? 'left' : 'right') : ly < 0 ? 'up' : 'down';
  }

  private focusable(it: NavItem): boolean {
    return it.isFocusable ? it.isFocusable() : true;
  }

  /** Nearest item in the pressed direction; perpendicular offset weighs more than distance. */
  private spatialPick(cur: NavItem, dir: NavDir, list: NavItem[]): NavItem | null {
    const a = cur.navBounds();
    let best: NavItem | null = null;
    let bestScore = Infinity;
    for (const it of list) {
      const b = it.navBounds();
      const { main, cross, overlap } = this.axis(a, b, dir);
      if (main <= 2) continue;
      const score = main + (overlap ? 0 : Math.abs(cross) * 2.4);
      if (score < bestScore) {
        bestScore = score;
        best = it;
      }
    }
    return best;
  }

  /** Nothing in that direction: jump to the far side, staying in the same row/column when possible. */
  private wrapPick(cur: NavItem, dir: NavDir, list: NavItem[]): NavItem | null {
    const a = cur.navBounds();
    let best: NavItem | null = null;
    let bestScore = Infinity;
    for (const it of list) {
      const b = it.navBounds();
      const { main, cross, overlap } = this.axis(a, b, dir);
      // Most negative 'main' = farthest in the opposite direction.
      const score = (overlap ? 0 : Math.abs(cross) * 3) + main * 0.5;
      if (main < -2 && score < bestScore) {
        bestScore = score;
        best = it;
      }
    }
    return best;
  }

  private axis(a: NavRect, b: NavRect, dir: NavDir): { main: number; cross: number; overlap: boolean } {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const horizontal = dir === 'left' || dir === 'right';
    const main = dir === 'right' ? dx : dir === 'left' ? -dx : dir === 'down' ? dy : -dy;
    const cross = horizontal ? dy : dx;
    // Overlapping on the perpendicular axis = same row / column.
    const overlap = horizontal
      ? Math.abs(dy) < (a.h + b.h) / 2 - 4
      : Math.abs(dx) < (a.w + b.w) / 2 - 4;
    return { main, cross, overlap };
  }
}
